import { useQuery } from "@tanstack/react-query";
import $api from "@/zapi-frontend/shared/api/axios";
import type { TLedgerFigure, TModelLedger } from "@/zapi-frontend/shared/types/modelLedger";
import { deriveRole } from "./ledgerFormat";

export const MODEL_LEDGER_QUERY_KEY = "project-model-ledger";

export const LEDGER_REFETCH_MS = 15000;

// A ledger stored before sources were structured carries `source` as the free text it was written
// as, and one stored before roles carries none. The route returns what is stored, so the shape is
// settled here, once, for the island, the search over it and the export alike — otherwise an older
// project shows an empty source, drops out of a search by its own source, and exports `source=""`.
const normalizeFigure = (figure: TLedgerFigure): TLedgerFigure => {
  const source =
    typeof (figure as { source?: unknown }).source === "string"
      ? { kind: null, ref: figure.source as unknown as string, url: null, title: null }
      : figure.source;
  const normalized = { ...figure, source };
  return { ...normalized, role: figure.role ?? deriveRole(normalized) };
};

export function useModelLedger(projectId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [MODEL_LEDGER_QUERY_KEY, projectId],
    queryFn: async () => {
      const response = await $api.get<{ data: TModelLedger | null }>(`/projects/${projectId}/model-ledger`);
      const ledger = response.data.data;
      return ledger ? { ...ledger, figures: ledger.figures.map(normalizeFigure) } : ledger;
    },
    enabled: !!projectId && enabled,
    staleTime: 60000,
    refetchInterval: LEDGER_REFETCH_MS
  });
}
